import { PlatformSourceMapper } from '@mappers'
import { mapPropertyOrDefault } from '@mappers/mapping-helper'
import { mapFulfillmentCenterFromApi } from '@mappers/fulfillmentCenter'
import { mapDraftShippingFromApi, initShippingConfigurations, mapDraftMultiSkuShippingConfigurationsFromApi } from '@mappers/receiving/draft/shippingConfiguration'
import { FulfillmentCenterModel } from '@/models/fulfillmentCenter'
import { WroPackagingType } from '@/models/receiving/draft/packagingType'
import { WroSkuType } from '@/models/receiving/draft/skuType'
import SortDefinitions from '@/models/receiving/sortDefinitions'
import { machineSteps } from '@/models/createWarehouseReceivingOrder/create-order-machine'
import { skuTypeEnum, skuTypeApiEnum } from '../../../enums/modules/creatingWarehouseReceivingOrder/skuType'
import { formatDate, maxDate, diffDates } from '@/utils/common'
import { mergePoundsAndOuncesIntoWeight } from '@/utils/unitConverters/weightConvertor'
import { mapSdsStatus } from '@/utils/productPropertiesMapping'

export const mapSortColumnToApi = (sortColumn) => SortDefinitions[sortColumn]

const mapPackagingTypeFromApi = ({
  id,
  name
}) => ({
  id,
  name
})

const mapLabelPurchaseTypeFromApi = ({
  isShipBobLabels
}) => ({
  id: isShipBobLabels ? 1 : 2
})

const mapOriginAddressFromApi = ({
  name,
  streetAddress1,
  streetAddress2,
  city,
  country,
  state,
  zipCode
}) => ({
  name,
  streetAddress1,
  streetAddress2,
  city,
  state,
  zipCode
})

const mapSKUTypeFromApi = ({
  id,
  name,
  isWroCreated
}) => ({
  id: isWroCreated ? (id - 1) : id,
  name
})

const mapDraftProductQuantityFromApi = ({
  amount,
  lotNumber,
  expirationDate
}) => ({
  amount,
  lotNumber,
  expirationDate: formatDate(expirationDate)
})

const mapPricingGroupFromApi = ({
  id,
  name,
  estimatedShippingCost,
  shipbobZoneId
}) => ({
  id,
  name,
  estimatedShippingCost,
  shipbobZoneId
})

const mapDraftProductFromApi = ({
  id,
  name,
  sku,
  platformId,
  isLotProduct,
  quantitiesToSend,
  isDangerousGoods,
  msdsUrl
}) => ({
  id,
  name,
  sku: sku || '',
  platform: mapPropertyOrDefault(PlatformSourceMapper.mapPlatformSourceFromAPI, platformId),
  isLotProduct,
  isDangerousGoods,
  sdsStatus: mapSdsStatus(isDangerousGoods, msdsUrl), 
  quantitiesToSend: quantitiesToSend.map(mapDraftProductQuantityFromApi)
})

const mapCurrentStep = (lastCompletedStep) => {
  const inBounds = machineSteps.length > 0 && lastCompletedStep >= 0 && lastCompletedStep <= machineSteps.length
  let index
  if (inBounds && lastCompletedStep === machineSteps.length) index = lastCompletedStep - 1
  else if (inBounds) index = lastCompletedStep
  else index = 0
  return machineSteps[index] && machineSteps[index].stateNode && (machineSteps[index].stateNode.name || '')
}

const mapProductsFromApi = (products) => products && Array.isArray(products) ? products.map(mapDraftProductFromApi) : []

const mapFromApi = ({
  id,
  userId,
  createdDate,
  fulfillmentCenter,
  finalDestinationFulfillmentCenterId,
  purchaseOrderNumber,
  products,
  shippingType,
  labelDetails,
  skuType,
  shippingConfigurations,
  multiSKUShippingConfigurations,
  estimatedArrivalDate,
  pricingGroup,
  billOfLading,
  lastCompletedStep,
  lastUpdatedDate,
  isWroCreated,
  uroIds
}) => ({
  id,
  userId,
  createdDate: formatDate(createdDate),
  fulfillmentCenter: fulfillmentCenter ? mapFulfillmentCenterFromApi({ Id: fulfillmentCenter.id, Name: fulfillmentCenter.name }) : new FulfillmentCenterModel(),
  finalDestinationFulfillmentCenterId: finalDestinationFulfillmentCenterId,
  purchaseOrderNumber,
  products: mapProductsFromApi(products),
  shippingConfigurations: shippingConfigurations && shippingConfigurations.length ? shippingConfigurations.map(mapDraftShippingFromApi) : initShippingConfigurations(products || []),
  multiSKUShippingConfigurations: mapDraftMultiSkuShippingConfigurationsFromApi(multiSKUShippingConfigurations, mapProductsFromApi(products)),
  packagingType: shippingType ? mapPackagingTypeFromApi({ id: shippingType.id, name: shippingType.name }) : new WroPackagingType(),
  skuType: skuType ? mapSKUTypeFromApi({ id: skuType.id, name: skuType.name, isWroCreated }) : new WroSkuType(),
  labelPurchaseType: labelDetails ? mapLabelPurchaseTypeFromApi({ isShipBobLabels: labelDetails.isShipBobLabels }) : null,
  originAddress: (labelDetails && labelDetails.address) ? mapOriginAddressFromApi(labelDetails.address) : null,
  estimatedArrivalDate: formatDate(estimatedArrivalDate),
  pricingGroup: pricingGroup ? mapPricingGroupFromApi(pricingGroup) : null,
  billOfLading,
  currentStep: mapCurrentStep(lastCompletedStep),
  furthestCompletedStep: lastCompletedStep,
  lastUpdatedDate: formatDate(lastUpdatedDate),
  wroCreated: isWroCreated,
  shippingServiceId: null,
  uroIds: uroIds
})

const mapBaseDraftFromApi = ({
  draftId,
  fulfillmentCenter,
  purchaseOrderNumber,
  createdDate,
  updateDate,
  expirationDate,
}) => ({
  id: draftId,
  fulfillmentCenter: fulfillmentCenter ? mapFulfillmentCenterFromApi({ Id: fulfillmentCenter.id, Name: fulfillmentCenter.name }) : new FulfillmentCenterModel(),
  purchaseOrderNumber,
  createdDate: formatDate(createdDate),
  lastUpdatedDate: formatDate(maxDate(updateDate, createdDate)),
  daysTilExpiration: diffDates(expirationDate, new Date()),
})

const mapDraftsFromApi = (drafts) => ({ wros: drafts ? drafts.map(mapBaseDraftFromApi) : [] })

const mapBoxItemForCreate = ({
  productId,
  lotNumber,
  expirationDate,
  quantityPerBox
}) => ({
  Quantity: quantityPerBox,
  InventoryId: productId,
  LotNumber: lotNumber || null,
  LotDate: expirationDate || null
})

const mapSingleSkuConfigurationforCreate = ({ productId, lotNumber, expirationDate, boxConfigurations }, isShipBobLabel) => {
  return boxConfigurations.flatMap(({ numberOfBoxes, quantityPerBox, length, width, height, weight }) => {
    const boxes = []
    for (let i = 0; i < numberOfBoxes; i++) {
      boxes.push({
        TrackingNumber: null,
        BoxItems: [mapBoxItemForCreate({
          productId,
          lotNumber,
          expirationDate,
          quantityPerBox
        })], // single-sku
        BoxDimensions: isShipBobLabel
          ? {
              length,
              width,
              height,
              weightOunces: mergePoundsAndOuncesIntoWeight(weight)
            }
          : null
      })
    }
    return boxes
  })
}

const mapMultipleSkuConfigurationforCreate = (multiSKUShippingConfigurations, hasSelectedShipBobLabels) => {
  const boxes = []

  multiSKUShippingConfigurations.forEach(msConfig => {
    const boxItems = []
    let numberOfBoxes = 0
    let boxDimensions = null
    msConfig.skuConfigurations.forEach(skuConfig => {
      numberOfBoxes = skuConfig.numberOfBoxes
      boxItems.push(mapBoxItemForCreate({
        productId: skuConfig.product.id,
        lotNumber: skuConfig.product.lotInformation.lotNumber,
        expirationDate: skuConfig.product.lotInformation.expirationDate,
        quantityPerBox: skuConfig.quantityPerBox
      }))
      const hasBoxDimensions = skuConfig.length && skuConfig.width && skuConfig.height && skuConfig.weight
      if (hasSelectedShipBobLabels && hasBoxDimensions) {
        boxDimensions = {
          length: skuConfig.length,
          width: skuConfig.width,
          height: skuConfig.height,
          weightOunces: mergePoundsAndOuncesIntoWeight(skuConfig.weight)
        }
      }
    })
    for (let i = 0; i < numberOfBoxes; i++) {
      boxes.push({
        TrackingNumber: null,
        BoxItems: boxItems,
        BoxDimensions: boxDimensions
      })
    }
  })

  return boxes
}

const typeOfSku = (skuType, shippingConfigurations, multiSKUShippingConfigurations, isShipBobLabel) => {
  if (skuType.id === skuTypeEnum.OneSKUPerContainer) {
    return shippingConfigurations.flatMap(x => mapSingleSkuConfigurationforCreate(x, isShipBobLabel))
  } else {
    return mapMultipleSkuConfigurationforCreate(multiSKUShippingConfigurations, isShipBobLabel)
  }
}

const mapToApiForCreate = ({
  fulfillmentCenter,
  purchaseOrderNumber,
  packagingType,
  skuType,
  shippingConfigurations,
  estimatedArrivalDate,
  multiSKUShippingConfigurations,
  originAddress,
  pricingGroup,
  destinationFulfillmentCenter
}) => ({
  FulfillmentCenterId: fulfillmentCenter.id,
  finalDestinationFulfillmentCenterId: destinationFulfillmentCenter && destinationFulfillmentCenter.id,
  purchaseOrderNumber,
  SourceId: 1, // hardcode: Regular
  PackageTypeId: packagingType.id,
  BoxPackagingTypeId: skuTypeApiEnum[skuType.id],
  Boxes: typeOfSku(skuType, shippingConfigurations, multiSKUShippingConfigurations, (pricingGroup && (typeof pricingGroup.id === 'number'))),
  ExpectedArrivalDate: estimatedArrivalDate || null,
  SendOptionTypeId: 1, // send on own
  OriginAddress: (pricingGroup && pricingGroup.id) ? originAddress : null,
  PricingGroup: (pricingGroup && pricingGroup.id)
    ? {
        Id: pricingGroup.id,
        Name: pricingGroup.name,
        EstimatedLabelCost: pricingGroup.estimatedShippingCost
      }
    : null
})

const mapToApiForCreateByShipOption = ({
  fulfillmentCenter,
  purchaseOrderNumber,
  packagingType,
  skuType,
  shippingConfigurations,
  estimatedArrivalDate,
  multiSKUShippingConfigurations,
  originAddress,
  pricingGroup,
  destinationFulfillmentCenter
}) => ({
  FulfillmentCenterId: fulfillmentCenter.id,
  finalDestinationFulfillmentCenterId: destinationFulfillmentCenter && destinationFulfillmentCenter.id,
  purchaseOrderNumber,
  SourceId: 1, // hardcode: Regular
  PackageTypeId: packagingType.id,
  BoxPackagingTypeId: skuTypeApiEnum[skuType.id],
  Boxes: typeOfSku(skuType, shippingConfigurations, multiSKUShippingConfigurations, (pricingGroup && (typeof pricingGroup.id === 'number'))),
  ExpectedArrivalDate: estimatedArrivalDate || null,
  SendOptionTypeId: 1, // send on own
  OriginAddress: (pricingGroup && pricingGroup.id) ? originAddress : null,
  PricingGroup: (pricingGroup && pricingGroup.id)
    ? {
        Id: pricingGroup.id,
        Name: pricingGroup.name,
        EstimatedLabelCost: pricingGroup.estimatedShippingCost
      }
    : null,
  shippingServiceId: (pricingGroup && pricingGroup.id) ? pricingGroup.id : null
})

export default {
  mapFromApi,
  mapToApiForCreate,
  mapToApiForCreateByShipOption,
  mapDraftsFromApi
}
